/**
 * Broadcast Queue — Очередь рассылок с ручным подтверждением
 * Дата: 13.02.2026
 * Обновлено: 14.02.2026 — TenantSelector, переход на переиспользуемые UI-компоненты
 * Обновлено: 10.04.26 — approve-all теперь async с progress dialog вместо длинного sync ожидания
 * 
 * Назначение:
 * - Показывает все сгенерированные рассылки, ожидающие одобрения
 * - Администратор может одобрить (и отправить), отклонить, или очистить очередь
 * - Суперадмин может переключить режим: "manual" / "auto"
 * - Отображает статистику по статусам (pending, approved, sent, rejected, etc.)
 * - Интегрирован с constraint-сервисами и schedule
 * 
 * Переиспользуемые компоненты:
 * - CheckboxTable — таблица с чекбоксами массового выбора
 * - SearchInput — поиск с debounce
 * - FilterSelect — выпадающие фильтры
 * - ConfirmDialog — диалог подтверждения (вместо window.confirm)
 * - StatusBadge — статус-бейджи
 * - EmptyState — пустое состояние
 * - Pagination — пагинация
 * - TenantSelector — выбор тенанта для SuperAdmin
 * 
 * Связан с:
 * - internal/handler/broadcast_queue.go
 * - internal/domain/service/broadcast/approve_all_job.go
 * - internal/domain/service/broadcast/processor.go
 * - internal/domain/entity/broadcast_queue.go
 */
import { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Trash2,
  RefreshCw,
  Send,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  CheckCheck,
  Radio,
} from 'lucide-react';
import { endOfDay, startOfDay, subDays, subMonths } from 'date-fns';
import { MainLayout } from '@/components/layout/MainLayout';
import { FlowNote } from '@/features/monitoring/FlowNote';
import { PageGuide } from '@/components/ui/help';
import {
  TenantSelector,
  SearchInput,
  FilterSelect,
  PageSizeSelect,
  DateRangePicker,
  EmptyState,
  Pagination,
  Button,
  LoadingState,
} from '@/components/ui';
import {
  getBroadcastQueueCapabilities,
} from '@/features/broadcast-queue/selectors';
import { BroadcastQueueDialogs } from '@/features/broadcast-queue/components/BroadcastQueueDialogs';
import { BroadcastQueueSummaryCards } from '@/features/broadcast-queue/components/BroadcastQueueSummaryCards';
import { BroadcastQueueTable } from '@/features/broadcast-queue/components/BroadcastQueueTable';
import { useBroadcastQueuePage } from '@/features/broadcast-queue/hooks/useBroadcastQueuePage';
import { useToast } from '@/hooks/useToast';
import { usePermissions } from '@/hooks/usePermissions';
import { useTenantScope } from '@/hooks/useTenantScope';
import { channelDisplayNames, statusLabels } from '@/types/broadcastQueue';
import { QUEUE_SOURCE_OPTIONS } from '@/features/broadcast-queue/queueSourceRegistry';
import { broadcastQueueHelp } from '@/content/help';

export function BroadcastQueuePage() {
  const semanticDatePresets = [
    { label: 'Сегодня', from: startOfDay(new Date()), to: endOfDay(new Date()) },
    { label: 'Вчера', from: startOfDay(subDays(new Date(), 1)), to: endOfDay(subDays(new Date(), 1)) },
    { label: 'Неделя', from: startOfDay(subDays(new Date(), 7)), to: endOfDay(new Date()) },
    { label: 'Месяц', from: startOfDay(subMonths(new Date(), 1)), to: endOfDay(new Date()) },
  ];

  const { showSuccess, showError, showWarning } = useToast();
  const { can } = usePermissions();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  const permissions = getBroadcastQueueCapabilities({ can, isSuperAdmin });
  const {
    items,
    summary,
    mode,
    modeDescription,
    broadcastingEnabled,
    modeWarnings,
    total,
    offset,
    setOffset,
    limit,
    loading,
    actionLoading,
    selectedIds,
    setSelectedIds,
    statusFilter,
    setStatusFilter,
    eventTypeFilter,
    setEventTypeFilter,
    searchQuery,
    setSearchQuery,
    createdFrom,
    setCreatedFrom,
    createdTo,
    setCreatedTo,
    confirmApproveAll,
    setConfirmApproveAll,
    confirmClear,
    setConfirmClear,
    confirmToggleMode,
    setConfirmToggleMode,
    setRejectReason,
    showRejectDialog,
    setShowRejectDialog,
    previewItemId,
    setPreviewItemId,
    bulkChannelSelect,
    setBulkChannelSelect,
    showBulkChannelResult,
    setShowBulkChannelResult,
    operationJob,
    closeOperationJob,
    isOperationJobWsConnected,
    selectedApproveableIds,
    selectedPendingIds,
    selectedChannelChangeableIds,
    pendingCount,
    channelChangeableCount,
    loadData,
    handleLimitChange,
    handleApprove,
    handleApproveAll,
    handleReject,
    handleClear,
    handleToggleMode,
    handleBulkChannelChange,
    selectAllPending,
    selectAllChannelChangeable,
  } = useBroadcastQueuePage({
    tenantId,
    showSuccess,
    showError,
    showWarning,
  });
  const [showGuide, setShowGuide] = useState(false);

  const eventTypeOptions = QUEUE_SOURCE_OPTIONS;
  const statusOptions = Object.keys(statusLabels).map((status) => ({
    value: status,
    label: statusLabels[status as keyof typeof statusLabels],
  }));

  // --- Header actions (TenantSelector + Refresh) ---
  const headerActions = (
    <div className="flex items-center gap-3">
      {isSuperAdmin && (
        <TenantSelector
          value={selectedTenantId || ''}
          onChange={setSelectedTenant}
          required={false}
          showLabel={false}
        />
      )}
      <Button
        variant="secondary"
        size="sm"
        onClick={loadData}
        disabled={actionLoading}
      >
        <RefreshCw className={`w-4 h-4 mr-1 ${actionLoading ? 'animate-spin' : ''}`} />
        Обновить
      </Button>
      <Button variant="secondary" size="sm" onClick={() => setShowGuide((open) => !open)}>
        Справка
      </Button>
    </div>
  );

  // --- Prompt to select tenant ---
  if (requireTenantSelection) return (
    <MainLayout headerTitle="Очередь рассылок" headerActions={headerActions}>
      <div className="p-6">
        <EmptyState
          type="noSettings"
          title="Выберите клинику"
          description="Для просмотра очереди рассылок выберите клинику в выпадающем списке выше."
        />
      </div>
    </MainLayout>
  );

  if (loading && items.length === 0) return (
    <MainLayout headerTitle="Очередь рассылок" headerActions={headerActions}>
      <LoadingState text="Загрузка очереди рассылок..." />
    </MainLayout>
  );

  return (
    <MainLayout headerTitle="Очередь рассылок" headerActions={headerActions}>
    <div className="space-y-3 p-4">
      {showGuide && (
        <>
          <FlowNote id="queue" />
          <PageGuide
            id="broadcast-queue-guide"
            title={broadcastQueueHelp.pageGuide.title}
            variant="info"
            expandedContent={
              broadcastQueueHelp.expandedGuide && (
                <div className="space-y-1.5">
                  {broadcastQueueHelp.expandedGuide.body.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                  {broadcastQueueHelp.expandedGuide.tips && (
                    <div className="mt-2 space-y-1">
                      {broadcastQueueHelp.expandedGuide.tips.map((tip, i) => (
                        <p key={i} className="flex items-start gap-1.5">
                          <span className="mt-px text-shell-primary">•</span>
                          <span>{tip}</span>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )
            }
          >
            {broadcastQueueHelp.pageGuide.body.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </PageGuide>
        </>
      )}

      {/* 24.10.2026 — режим компактной строкой. Подтверждение смены остаётся в диалоге. */}
      {permissions.canManageMode && (
        <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-2 ${mode === 'manual' ? 'border-shell-warning/30 bg-shell-warning-soft' : 'border-shell-border bg-shell-surface'}`}>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-shell-strong">
              {mode === 'manual' ? 'Ручное одобрение' : 'Автоматическая отправка'}
            </p>
            <p className="text-xs leading-4 text-shell-muted">{modeDescription}</p>
            {!broadcastingEnabled && (
              <p className="mt-1 text-xs font-semibold text-shell-danger">Включен глобальный Killswitch</p>
            )}
          </div>
          <Button
            onClick={() => setConfirmToggleMode(true)}
            disabled={actionLoading}
            variant="secondary"
            size="sm"
          >
            {mode === 'manual' ? (
              <><ToggleRight className="w-4 h-4 mr-1" /> Включить автоматический</>
            ) : (
              <><ToggleLeft className="w-4 h-4 mr-1" /> Включить ручной</>
            )}
          </Button>
        </div>
      )}

      {modeWarnings.length > 0 && (
        <div className="space-y-3">
          {modeWarnings.map((warning) => {
            const isCritical = warning.severity === 'critical';
            return (
              <div
                key={`${warning.code}-${warning.message}`}
                className={`rounded-xl border px-3 py-2 ${isCritical ? 'border-shell-danger/30 bg-shell-danger-soft' : 'border-shell-warning/30 bg-shell-warning-soft'}`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`mt-0.5 h-4 w-4 ${isCritical ? 'text-shell-danger' : 'text-shell-warning'}`} />
                  <div className="space-y-1">
                    <p className={`text-sm font-semibold ${isCritical ? 'text-shell-danger' : 'text-shell-warning'}`}>
                      {warning.message}
                    </p>
                    {warning.code === 'global_killswitch_enabled' && (
                      <p className="text-xs text-shell-danger">Включен глобальный Killswitch</p>
                    )}
                    {typeof warning.affected_items === 'number' && warning.affected_items > 0 && (
                      <p className="text-xs text-shell-muted">
                        Затронуто сообщений: {warning.affected_items}
                      </p>
                    )}
                    {warning.affected_templates && warning.affected_templates.length > 0 && (
                      <p className="text-xs text-shell-muted">
                        Шаблоны: {warning.affected_templates.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <BroadcastQueueSummaryCards summary={summary} />
      )}

      {/* Search + Filters + Bulk Actions */}
      <div className="space-y-3 rounded-xl border border-shell-border bg-shell-surface p-3">
        {/* Row 1: Search + Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск по ФИО или телефону..."
            size="sm"
            className="w-64"
            debounceMs={400}
          />

          <FilterSelect
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            options={statusOptions}
            allOptionLabel="Все статусы"
            size="sm"
          />

          <FilterSelect
            value={eventTypeFilter}
            onChange={(v) => setEventTypeFilter(v)}
            options={eventTypeOptions}
            allOptionLabel="Все типы"
            size="sm"
          />

          <PageSizeSelect
            value={limit}
            onChange={handleLimitChange}
            options={[50, 100, 250, 500]}
            disabled={loading || actionLoading}
          />

          {loading && <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />}
        </div>

        <div className="border-t pt-3">
          <DateRangePicker
            from={createdFrom}
            to={createdTo}
            presets={semanticDatePresets}
            onChange={(from, to) => {
              setCreatedFrom(from);
              setCreatedTo(to);
            }}
          />
          <p className="mt-2 text-xs leading-4 text-shell-muted" title="Для напоминания за 24 часа «Сегодня» — это записи на завтра. Для 2 часов и обратной связи — приёмы сегодня. Тип «24 часа» и «2 часа» лучше не смешивать.">
            Период относится к смыслу шаблона, а не к моменту постановки в очередь. «Отправлено» не значит «доставлено».
          </p>
        </div>

        {/* Row 2: Bulk Actions */}
        {permissions.canUseSelection && (
          <div className="flex flex-wrap items-center gap-2 border-t border-shell-border pt-2">
            <span className="mr-1 text-sm text-shell-muted">
              Выбрано: <strong className="text-shell-strong">{selectedIds.size}</strong>
            </span>

            {permissions.canApproveSelected && (
              <Button
                variant="secondary"
                size="sm"
                onClick={selectAllPending}
                disabled={actionLoading}
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Выбрать ожидающие ({pendingCount})
              </Button>
            )}

            {permissions.canChangeChannel && (
              <Button
                variant="secondary"
                size="sm"
                onClick={selectAllChannelChangeable}
                disabled={actionLoading}
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Выбрать для смены канала ({channelChangeableCount})
              </Button>
            )}

            {selectedIds.size > 0 && permissions.canApproveSelected && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleApprove}
                disabled={actionLoading || selectedApproveableIds.length === 0}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Одобрить ({selectedApproveableIds.length})
              </Button>
            )}

            {selectedIds.size > 0 && permissions.canApproveSelected && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  if (selectedPendingIds.length === 0) {
                    showWarning('Для отклонения выберите сообщения со статусом «Ожидает одобрения»');
                    return;
                  }
                  setShowRejectDialog(true);
                }}
                disabled={actionLoading || selectedPendingIds.length === 0}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Отклонить ({selectedPendingIds.length})
              </Button>
            )}

            {/* 12.03.2026 — Bulk channel change */}
            {selectedIds.size > 0 && permissions.canChangeChannel && (
              <div className="ml-2 flex items-center gap-1 border-l border-shell-border pl-2">
                <select
                  value={bulkChannelSelect}
                  onChange={(e) => setBulkChannelSelect(e.target.value)}
                  disabled={actionLoading || selectedChannelChangeableIds.length === 0}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                >
                  <option value="">Канал...</option>
                  {Object.entries(channelDisplayNames).filter(([k]) => k !== 'none').map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkChannelChange}
                  disabled={actionLoading || selectedChannelChangeableIds.length === 0 || !bulkChannelSelect}
                  title="Сменить канал для выбранных"
                >
                  <Radio className="w-4 h-4 mr-1" />
                  Сменить канал ({selectedChannelChangeableIds.length})
                </Button>
              </div>
            )}

            <div className="flex-1" />

            {permissions.canApproveAll && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setConfirmApproveAll(true)}
                  disabled={actionLoading || pendingCount === 0}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Одобрить все ({pendingCount})
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmClear(true)}
                  disabled={actionLoading || pendingCount === 0}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Очистить
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <BroadcastQueueTable
        items={items}
        mode={mode}
        searchQuery={searchQuery}
        canUseSelection={permissions.canUseSelection}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onPreview={setPreviewItemId}
      />

      {/* Pagination */}
      {total > limit && (
        <Pagination
          offset={offset}
          limit={limit}
          total={total}
          onOffsetChange={setOffset}
          showPageNumbers
        />
      )}
    </div>

    <BroadcastQueueDialogs
      confirmApproveAll={confirmApproveAll}
      onCloseApproveAll={() => setConfirmApproveAll(false)}
      onConfirmApproveAll={handleApproveAll}
      confirmClear={confirmClear}
      onCloseClear={() => setConfirmClear(false)}
      onConfirmClear={handleClear}
      confirmToggleMode={confirmToggleMode}
      onCloseToggleMode={() => setConfirmToggleMode(false)}
      onConfirmToggleMode={handleToggleMode}
      showRejectDialog={showRejectDialog}
      onCloseRejectDialog={() => {
        setShowRejectDialog(false);
        setRejectReason('');
      }}
      onConfirmReject={handleReject}
      pendingCount={pendingCount}
      selectedPendingCount={selectedPendingIds.length}
      actionLoading={actionLoading}
      mode={mode}
      bulkChannelResult={showBulkChannelResult}
      onCloseBulkChannelResult={() => setShowBulkChannelResult(null)}
      previewItemId={previewItemId}
      tenantId={tenantId || undefined}
      canChangeChannel={permissions.canChangeChannel}
      onQueueChanged={loadData}
      onClosePreview={() => setPreviewItemId(null)}
      operationJob={operationJob}
      isOperationJobWsConnected={isOperationJobWsConnected}
      onCloseOperationJob={closeOperationJob}
    />

    </MainLayout>
  );
}
